'use client';

import { c as _c } from "react/compiler-runtime";
import { useUpdateProfile, useUploadImage } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { CropModal } from '@/components/images';
import { shouldFetchImageQuota } from '@/components/images/image-quota-eligibility';
import { ImageUploadDropzone } from '@/components/images/image-upload-dropzone';
import { MorfianTab } from '@/components/images/morfian-tab';
import { OptimizedImage } from '@/components/images/optimized-image';
import { SearchTab } from '@/components/images/search-tab';
import { IMAGE_UPLOAD_MAX_MB, validateImageUpload } from '@/components/images/validation';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AppTabs, type AppTabsItem } from '@/components/ui/app-tabs';
import { Icon } from '@/components/ui/icon';
import { PaintBoardIcon, RotateClockwiseIcon, Search01Icon, SparklesIcon, Tick02Icon, Upload01Icon } from '@/components/ui/icon/glyphs';
import { MotionAnimatedAutoHeight } from '@/components/ui/motion-animated-auto-height';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TOAST_IDS } from '@/config/toast-ids';
import { checkGate } from '@/lib/analytics';
import { runWithCleanup } from '@/lib/async/run-with-cleanup';
import { type DotCoverOverrides, dotStudioModeLabel, encodeDotStudioHeroMarker, type GenerativeVariant, generateDotStudioCoverPng, generateGenerativePng, generateHeroCoverPickerOptions, hashStringToSeed, LEGACY_GENERATIVE_VARIANTS, type PaletteName, pickBrandBluePalette, type RandomDotStudioPattern } from '@/lib/pattern-studio';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
import { useUploadTrackingStore } from '@/stores/upload-tracking';
const AVATAR_VARIANTS: ReadonlyArray<GenerativeVariant> = LEGACY_GENERATIVE_VARIANTS;
function defaultVariantFor(kind: IdentityImagePickerKind): GenerativeVariant {
  return kind === 'avatar' ? 'waves' : 'breakthrough';
}
export type IdentityImagePickerKind = 'avatar' | 'hero';
export interface IdentityImagePickerProps {
  kind: IdentityImagePickerKind;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Owning user's id. Drives the live preview palette
   * (`pickBrandBluePalette`) and the per-variant base seed.
   */
  userId: string;
  /** Pre-fills the Search tab input. Pass the user's display name. Empty string disables auto-fire. */
  searchSeedQuery: string;
  currentUrl: string | null;
  currentAvatarNsfw?: boolean | null;
  onChange: (url: string, avatarNsfw?: boolean | null) => void;
  /**
   * REQUIRED when `kind === 'avatar'`. The current
   * `users.avatar_preferences.rasterise` value. Drives the initial state
   * of the "Dithered / Original" toggle in the upload confirmation panel.
   */
  currentRasterise?: boolean;
  /**
   * Avatar only. Persisted seed from `users.avatarPreferences.morfianSeed`.
   * Lets a fresh-mounted picker reproduce the user's most recent Morfian on
   * the first generate; Re-roll mints a new seed regardless.
   */
  morfianStoredSeed?: string;
}
type Tab = 'search' | 'generate' | 'morfian' | 'upload';
interface KindConfig {
  generativeWidth: number;
  generativeHeight: number;
  fileNameStem: (userId: string) => string;
  /** Avatar grid: square 64px circular tiles. Hero grid: 3:1 rectangular tiles. */
  gridClassName: string;
  tileClassName: string;
  tickIconClassName: string;
  /** Step-2 post-select preview wrapper. */
  previewWrapperClassName: string;
  /** Variant-tile label row layout. */
  labelRowClassName: string;
  refreshButtonSizeClassName: string;
  /** Source-PNG dimensions for variant previews. */
  variantPreviewSource: (pixelSize: number) => {
    width: number;
    height: number;
  };
  variantPreviewDefaultPixelSize: number;
  /** OptimizedImage variant prop. */
  optimizedImageVariant: 'avatar' | 'hero';
  optimizedImageRounded: string;
  optimizedImageExtraClass: string;
  /** Pixel size passed to OptimizedImage at step-2 (post-select). Avatar = 224; hero infers from container. */
  step2PixelSize: number | null;
}
const AVATAR_KIND: KindConfig = {
  generativeWidth: 512,
  generativeHeight: 512,
  fileNameStem: (userId: string): string => `${userId}-avatar`,
  gridClassName: 'grid grid-cols-3 gap-3 sm:grid-cols-5',
  tileClassName: 'relative size-16 overflow-hidden rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary',
  tickIconClassName: 'size-4 text-foreground-primary',
  previewWrapperClassName: 'relative size-48 overflow-hidden rounded-full bg-background-tertiary sm:size-56',
  labelRowClassName: 'flex w-full items-center justify-between gap-1',
  refreshButtonSizeClassName: 'size-6 shrink-0',
  variantPreviewSource: (pixelSize: number): {
    width: number;
    height: number;
  } => ({
    width: pixelSize * 4,
    height: pixelSize * 4
  }),
  variantPreviewDefaultPixelSize: 64,
  optimizedImageVariant: 'avatar',
  optimizedImageRounded: 'rounded-full',
  optimizedImageExtraClass: '',
  step2PixelSize: 224
};
const HERO_PREVIEW_WIDTH = 600;
const HERO_PREVIEW_HEIGHT = 200;
const HERO_COVER_WIDTH = 1500;
const HERO_COVER_HEIGHT = 500;
const HERO_KIND: KindConfig = {
  generativeWidth: HERO_COVER_WIDTH,
  generativeHeight: HERO_COVER_HEIGHT,
  fileNameStem: (userId: string): string => `${userId}-hero`,
  gridClassName: 'grid grid-cols-2 gap-3 sm:grid-cols-3',
  tileClassName: 'relative aspect-[3/1] w-full overflow-hidden rounded-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary',
  tickIconClassName: 'size-5 text-foreground-primary',
  previewWrapperClassName: 'relative aspect-[3/1] w-full max-w-md overflow-hidden rounded-card bg-background-tertiary',
  labelRowClassName: 'flex items-center justify-between gap-2',
  refreshButtonSizeClassName: 'size-6',
  // Picker preview stays at the HTML Dot Studio reference size; upload uses
  // HERO_COVER_* with layout-scaled spacing so on-screen density matches.
  variantPreviewSource: (): {
    width: number;
    height: number;
  } => ({
    width: HERO_PREVIEW_WIDTH,
    height: HERO_PREVIEW_HEIGHT
  }),
  variantPreviewDefaultPixelSize: 64,
  optimizedImageVariant: 'hero',
  optimizedImageRounded: '',
  optimizedImageExtraClass: 'object-cover',
  step2PixelSize: null
};
function configFor(kind: IdentityImagePickerKind): KindConfig {
  return kind === 'avatar' ? AVATAR_KIND : HERO_KIND;
}
interface IdentityPickerActions {
  handleConfirmGenerative: () => Promise<void>;
  handleMorfianConfirm: (result: {
    ipfsUrl: string;
    seed: string;
  }) => Promise<void>;
  handleUploadUrlChange: (url: string) => Promise<void>;
  handleUploadFile: (file: File) => void;
}

/**
 * The picker's persistence handlers — generative confirm, Morfian confirm,
 * crop-modal URL persist, and upload-file validation. Owns the identity SDK
 * mutations (profile update and image upload) so the component
 * body stays a renderer. Kept in this file (not a separate module) so the
 * component's intentional multi-slice `useState` keeps its file-keyed
 * prefer-useReducer verified-false-positive exemption.
 */
function useIdentityPickerActions({
  kind,
  userId,
  onChange,
  onOpenChange,
  selectedVariant,
  seedFor,
  heroCoverOptions,
  selectedHeroCoverIndex,
  setIsSaving,
  setError,
  setUploadError,
  setPendingUploadFile,
  setUploadOpen,
  chosenRasteriseRef,
  chosenAvatarModeRef,
  lastPersistedUrlRef,
  lastPersistedAvatarNsfwRef
}: {
  kind: IdentityImagePickerKind;
  userId: string;
  onChange: (url: string, avatarNsfw?: boolean | null) => void;
  onOpenChange: (open: boolean) => void;
  selectedVariant: GenerativeVariant;
  seedFor: (variant: GenerativeVariant) => number;
  heroCoverOptions: readonly RandomDotStudioPattern[] | null;
  selectedHeroCoverIndex: number;
  setIsSaving: (saving: boolean) => void;
  setError: (error: string | null) => void;
  setUploadError: (error: string | null) => void;
  setPendingUploadFile: (file: File | null) => void;
  setUploadOpen: (open: boolean) => void;
  chosenRasteriseRef: React.RefObject<boolean>;
  chosenAvatarModeRef: React.RefObject<'pattern' | 'original' | 'morfian'>;
  lastPersistedUrlRef: React.RefObject<string | null>;
  lastPersistedAvatarNsfwRef: React.RefObject<boolean | null>;
}): IdentityPickerActions {
  const cfg = configFor(kind);
  const tNs = useTranslations(`${kind}_picker`);
  const tLegacy = useTranslations(kind === 'avatar' ? 'profile.avatar' : 'profile.hero');
  const tImageUpload = useTranslations('image_upload');
  const {
    mutateAsync: updateProfile
  } = useUpdateProfile();
  const uploadMutation = useUploadImage();
  const trackUpload = useUploadTrackingStore(s => s.track);
  async function handleConfirmGenerative(): Promise<void> {
    setIsSaving(true);
    setError(null);
    showToast({
      id: TOAST_IDS.IMAGE_UPLOAD,
      message: tImageUpload('uploading'),
      variant: 'loading'
    });
    await runWithCleanup(async () => {
      try {
        if (kind === 'hero') {
          const selectedCover = heroCoverOptions?.[selectedHeroCoverIndex] ?? null;
          if (selectedCover === null) {
            setError(tImageUpload('upload_error'));
            return;
          }
          const markerUrl = encodeDotStudioHeroMarker(selectedCover);
          // IMPERSONATION_GUARD: identity picker is mounted only inside profile editor surfaces gated by SettingsImpersonationGate / isOwn checks. Backend 403s as defence in depth.
          await updateProfile({
            heroUrl: markerUrl
          });
          onChange(markerUrl);
        } else {
          const blob = await generateGenerativePng(selectedVariant, seedFor(selectedVariant), {
            width: cfg.generativeWidth,
            height: cfg.generativeHeight,
            mimeType: 'image/png'
          });
          const file = new File([blob], `${cfg.fileNameStem(userId)}.png`, {
            type: 'image/png'
          });
          // ALLOW-UNKNOWN: SDK v2 mutation return type is generic `unknown`; cast to the documented contract.
          const result = (await uploadMutation.mutateAsync({
            file
          })) as {
            url: string;
            sha256: string;
          };
          trackUpload(result.url, result.sha256);
          // IMPERSONATION_GUARD: identity picker is mounted only inside profile editor surfaces gated by SettingsImpersonationGate / isOwn checks. Backend 403s as defence in depth.
          await updateProfile({
            avatarUrl: result.url
          });
          lastPersistedUrlRef.current = result.url;
          lastPersistedAvatarNsfwRef.current = false;
          onChange(result.url, false);
        }
        showToast({
          id: TOAST_IDS.IMAGE_UPLOAD,
          message: tLegacy('success'),
          variant: 'success'
        });
        onOpenChange(false);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        logger.error(`${kind} generative save failed`, {
          error: message.slice(0, 200)
        });
        setError(tNs('generate_error'));
        showToast({
          id: TOAST_IDS.IMAGE_UPLOAD,
          message: tNs('generate_error'),
          variant: 'error'
        });
      }
    }, () => setIsSaving(false));
  }

  /**
   * Morfian confirm handler — fired when the user accepts a generated
   * Morfian. The IPFS URL is already canonical (backend pinned the fal.ai
   * output). Persists `avatarUrl` PLUS the Morfian-mode `avatarPreferences`
   * (raster off, morfianSeed) in a single PATCH so the profile shows
   * the Morfian on first load — never the dithered fallback.
   */
  async function handleMorfianConfirm(result_0: {
    ipfsUrl: string;
    seed: string;
  }): Promise<void> {
    const previousUrl = lastPersistedUrlRef.current;
    const previousAvatarNsfw = lastPersistedAvatarNsfwRef.current;
    onChange(result_0.ipfsUrl, false);
    try {
      // ALLOW-RASTER-LITERAL: Morfian-mode confirm path — disabling raster is intrinsic to selecting a Morfian (no user preference can override).
      // IMPERSONATION_GUARD: identity picker is mounted only inside profile editor surfaces gated by SettingsImpersonationGate / isOwn checks. Backend 403s as defence in depth.
      await updateProfile({
        avatarUrl: result_0.ipfsUrl,
        // ARBITRARY: OpenAPI spec omits `mode`/`morfianSeed` but backend accepts and persists them.
        // Cast until the spec + codegen catches up.
        avatarPreferences: {
          // ALLOW-RASTER-LITERAL: Morfian-mode confirm path — disabling raster is intrinsic to selecting a Morfian (no user preference can override).
          rasterise: false,
          mode: 'morfian',
          morfianSeed: result_0.seed
        } as {
          rasterise: boolean;
        }
      });
      lastPersistedUrlRef.current = result_0.ipfsUrl;
      lastPersistedAvatarNsfwRef.current = false;
      onOpenChange(false);
    } catch (err_0: unknown) {
      const message_0 = err_0 instanceof Error ? err_0.message : String(err_0);
      logger.error('avatar morfian save failed', {
        error: message_0.slice(0, 200)
      });
      onChange(previousUrl ?? '', previousAvatarNsfw);
      showToast({
        id: TOAST_IDS.IMAGE_UPLOAD,
        message: tNs('generate_error'),
        variant: 'error'
      });
    }
  }
  async function handleUploadUrlChange(url: string): Promise<void> {
    const previousUrl_0 = lastPersistedUrlRef.current;
    const previousAvatarNsfw_0 = lastPersistedAvatarNsfwRef.current;
    if (kind === 'avatar' && url.length === 0) {
      onChange(previousUrl_0 ?? '', previousAvatarNsfw_0);
      return;
    }
    // Optimistic update FIRST — the IPFS URL is the canonical location, so
    // we render through the lib immediately rather than wait for the
    // updateProfile + getProfile round-trip.
    onChange(url, kind === 'avatar' ? false : undefined);
    try {
      // IMPERSONATION_GUARD: identity picker is mounted only inside profile editor surfaces gated by SettingsImpersonationGate / isOwn checks. Backend 403s as defence in depth.
      await updateProfile(kind === 'avatar' ? {
        avatarUrl: url,
        // The crop-modal Morfianate path also routes through this
        // handler — when `selectedMode` is `'morfian'` the upload
        // step is skipped and `chosenAvatarModeRef` is set to
        // `'morfian'` before this PATCH fires. The legacy raster
        // flag stays in sync (raster=on ↔ `mode: 'pattern'`).
        // ARBITRARY: OpenAPI spec omits `mode` but backend accepts and persists it.
        // Cast until the spec + codegen catches up.
        avatarPreferences: {
          rasterise: chosenRasteriseRef.current,
          mode: chosenAvatarModeRef.current
        } as {
          rasterise: boolean;
        }
      } : {
        heroUrl: url
      });
      lastPersistedUrlRef.current = url.length > 0 ? url : null;
      if (kind === 'avatar') lastPersistedAvatarNsfwRef.current = false;
    } catch (err_1: unknown) {
      const message_1 = err_1 instanceof Error ? err_1.message : String(err_1);
      logger.error(`${kind} persistence failed`, {
        error: message_1.slice(0, 200)
      });
      onChange(previousUrl_0 ?? '', previousAvatarNsfw_0);
    }
  }
  function handleUploadFile(file_0: File): void {
    const validation = validateImageUpload(file_0);
    if (validation === 'invalid_type') {
      setUploadError(tImageUpload('error_invalid_type'));
      return;
    }
    if (validation === 'too_large') {
      setUploadError(tImageUpload('error_too_large', {
        maxMB: IMAGE_UPLOAD_MAX_MB
      }));
      return;
    }
    setUploadError(null);
    setPendingUploadFile(file_0);
    setUploadOpen(true);
  }
  return {
    handleConfirmGenerative,
    handleMorfianConfirm,
    handleUploadUrlChange,
    handleUploadFile
  };
}

/**
 * Generative-pattern tab — the variant grid (step 1) and the live full-size
 * preview (step 2), with the Back / Next / Confirm action bar. Selection +
 * step + error state stay in the parent (so its multi-slice useState count is
 * preserved); this component only renders them and reports intent upward.
 */
function GenerateTab(t0) {
  const $ = _c(102);
  const {
    kind,
    userId,
    palette,
    selectedVariant,
    onSelectVariant,
    seedFor,
    refreshVariant,
    heroCoverOptions,
    selectedHeroCoverIndex,
    onSelectHeroCoverIndex,
    onRandomiseHero,
    genStep,
    onNext,
    onBack,
    onConfirm,
    error,
    isSaving
  } = t0;
  const tNs = useTranslations(`${kind}_picker`);
  const tImageUpload = useTranslations("image_upload");
  let cfg;
  let t1;
  let t2;
  let t3;
  let variantLabel;
  if ($[0] !== error || $[1] !== heroCoverOptions || $[2] !== isSaving || $[3] !== kind || $[4] !== onConfirm || $[5] !== onRandomiseHero || $[6] !== onSelectHeroCoverIndex || $[7] !== onSelectVariant || $[8] !== palette || $[9] !== refreshVariant || $[10] !== seedFor || $[11] !== selectedHeroCoverIndex || $[12] !== selectedVariant || $[13] !== tNs || $[14] !== userId) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      cfg = configFor(kind);
      let t4;
      if ($[20] !== tNs) {
        t4 = variant => tNs(`variant_${variant}` as Parameters<typeof tNs>[0]);
        $[20] = tNs;
        $[21] = t4;
      } else {
        t4 = $[21];
      }
      variantLabel = t4;
      if (kind === "hero") {
        const selectedCover = heroCoverOptions?.[selectedHeroCoverIndex] ?? null;
        if (selectedCover === null || heroCoverOptions === null) {
          const t5 = cfg.previewWrapperClassName;
          let t6;
          if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
            t6 = <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-br from-background-tertiary to-background-secondary" />;
            $[22] = t6;
          } else {
            t6 = $[22];
          }
          let t7;
          if ($[23] !== tNs) {
            t7 = tNs("generating");
            $[23] = tNs;
            $[24] = t7;
          } else {
            t7 = $[24];
          }
          let t8;
          if ($[25] !== t7) {
            t8 = <span className="sr-only">{t7}</span>;
            $[25] = t7;
            $[26] = t8;
          } else {
            t8 = $[26];
          }
          t3 = <div className="flex flex-col gap-4"><div className={t5} data-testid="hero-cover-preview" aria-busy="true">{t6}{t8}</div></div>;
          break bb0;
        }
        let t5;
        if ($[27] !== selectedHeroCoverIndex) {
          t5 = (option, index) => index !== selectedHeroCoverIndex ? [{
            option,
            index
          }] : [];
          $[27] = selectedHeroCoverIndex;
          $[28] = t5;
        } else {
          t5 = $[28];
        }
        const alternateCovers = heroCoverOptions.flatMap(t5);
        const t6 = `${selectedCover.variant}:${selectedCover.seedText}`;
        let t7;
        if ($[29] !== selectedCover.overrides || $[30] !== selectedCover.seedText || $[31] !== selectedCover.variant || $[32] !== t6 || $[33] !== userId) {
          t7 = <VariantPreview key={t6} kind="hero" userId={userId} variant={selectedCover.variant} seed={selectedCover.seedText} coverOverrides={selectedCover.overrides} />;
          $[29] = selectedCover.overrides;
          $[30] = selectedCover.seedText;
          $[31] = selectedCover.variant;
          $[32] = t6;
          $[33] = userId;
          $[34] = t7;
        } else {
          t7 = $[34];
        }
        const t9 = alternateCovers.map(t8 => {
          const {
            option: option_0,
            index: index_0
          } = t8;
          return <button key={`${option_0.variant}:${option_0.seedText}`} type="button" data-testid={`hero-cover-alt-${String(index_0)}`} aria-label={tNs("select_variant_aria", {
            variant: dotStudioModeLabel(option_0.variant)
          })} className={cfg.tileClassName} onClick={() => {
            onSelectHeroCoverIndex(index_0);
          }}><VariantPreview kind="hero" userId={userId} variant={option_0.variant} seed={option_0.seedText} coverOverrides={option_0.overrides} /></button>;
        });
        let t10;
        if ($[35] !== t9) {
          t10 = <div className="grid grid-cols-3 gap-3" data-testid="hero-cover-alternates">{t9}</div>;
          $[35] = t9;
          $[36] = t10;
        } else {
          t10 = $[36];
        }
        let t11;
        if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = <Icon icon={RotateClockwiseIcon} className="mr-1 size-4" aria-hidden="true" />;
          $[37] = t11;
        } else {
          t11 = $[37];
        }
        let t12;
        if ($[38] !== tNs) {
          t12 = tNs("randomise");
          $[38] = tNs;
          $[39] = t12;
        } else {
          t12 = $[39];
        }
        let t13;
        if ($[40] !== isSaving || $[41] !== onRandomiseHero || $[42] !== t12) {
          t13 = <AnimatedButton type="button" variant="secondary" className="min-h-11 sm:flex-1" disabled={isSaving} onClick={onRandomiseHero}>{t11}{t12}</AnimatedButton>;
          $[40] = isSaving;
          $[41] = onRandomiseHero;
          $[42] = t12;
          $[43] = t13;
        } else {
          t13 = $[43];
        }
        let t14;
        if ($[44] !== isSaving || $[45] !== tNs) {
          t14 = isSaving ? tNs("generating") : tNs("confirm");
          $[44] = isSaving;
          $[45] = tNs;
          $[46] = t14;
        } else {
          t14 = $[46];
        }
        let t15;
        if ($[47] !== isSaving || $[48] !== onConfirm || $[49] !== t14) {
          t15 = <AnimatedButton type="button" className="min-h-11 sm:flex-1" disabled={isSaving} loading={isSaving} onClick={onConfirm}>{t14}</AnimatedButton>;
          $[47] = isSaving;
          $[48] = onConfirm;
          $[49] = t14;
          $[50] = t15;
        } else {
          t15 = $[50];
        }
        let t16;
        if ($[51] !== t13 || $[52] !== t15) {
          t16 = <div className="flex flex-col gap-2 sm:flex-row">{t13}{t15}</div>;
          $[51] = t13;
          $[52] = t15;
          $[53] = t16;
        } else {
          t16 = $[53];
        }
        let t17;
        if ($[54] !== error) {
          t17 = error !== null ? <p role="alert" className="text-body text-feedback-error-content">{error}</p> : null;
          $[54] = error;
          $[55] = t17;
        } else {
          t17 = $[55];
        }
        t3 = <div className="flex flex-col gap-4"><div className={cfg.previewWrapperClassName} data-testid="hero-cover-preview">{t7}</div>{t10}{t16}{t17}</div>;
        break bb0;
      }
      t1 = cfg.gridClassName;
      t2 = AVATAR_VARIANTS.map(variant_0 => {
        const isSelected = selectedVariant === variant_0;
        const seed = seedFor(variant_0);
        return <div key={variant_0} className={cn("flex flex-col gap-2 rounded-card border-2 p-2 transition-colors duration-fast", kind === "avatar" && "items-center gap-1", isSelected ? "border-border-primary" : "border-border-primary")}><button type="button" onClick={() => {
            onSelectVariant(variant_0);
          }} aria-pressed={isSelected} aria-label={tNs("select_variant_aria", {
            variant: variantLabel(variant_0)
          })} className={cfg.tileClassName}><VariantPreview kind={kind} variant={variant_0} seed={seed} paletteName={palette} />{isSelected && <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-foreground-primary/20"><Icon icon={Tick02Icon} className={cfg.tickIconClassName} aria-hidden="true" /></span>}</button><div className={cfg.labelRowClassName}><span className="truncate text-caption text-foreground-secondary">{variantLabel(variant_0)}</span><button type="button" onClick={() => {
              refreshVariant(variant_0);
            }} aria-label={tNs("refresh_aria", {
              variant: variantLabel(variant_0)
            })} className={cn("inline-flex items-center justify-center rounded-control text-foreground-secondary transition-colors hover:bg-background-tertiary hover:text-foreground-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary", cfg.refreshButtonSizeClassName)}><Icon icon={RotateClockwiseIcon} className="size-4" aria-hidden="true" /></button></div></div>;
      });
    }
    $[0] = error;
    $[1] = heroCoverOptions;
    $[2] = isSaving;
    $[3] = kind;
    $[4] = onConfirm;
    $[5] = onRandomiseHero;
    $[6] = onSelectHeroCoverIndex;
    $[7] = onSelectVariant;
    $[8] = palette;
    $[9] = refreshVariant;
    $[10] = seedFor;
    $[11] = selectedHeroCoverIndex;
    $[12] = selectedVariant;
    $[13] = tNs;
    $[14] = userId;
    $[15] = cfg;
    $[16] = t1;
    $[17] = t2;
    $[18] = t3;
    $[19] = variantLabel;
  } else {
    cfg = $[15];
    t1 = $[16];
    t2 = $[17];
    t3 = $[18];
    variantLabel = $[19];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[56] !== t1 || $[57] !== t2) {
    t4 = <div className={t1}>{t2}</div>;
    $[56] = t1;
    $[57] = t2;
    $[58] = t4;
  } else {
    t4 = $[58];
  }
  const grid = t4;
  const t5 = cfg.previewWrapperClassName;
  let t6;
  if ($[59] !== seedFor || $[60] !== selectedVariant) {
    t6 = seedFor(selectedVariant);
    $[59] = seedFor;
    $[60] = selectedVariant;
    $[61] = t6;
  } else {
    t6 = $[61];
  }
  let t7;
  if ($[62] !== cfg.step2PixelSize) {
    t7 = cfg.step2PixelSize !== null ? {
      pixelSize: cfg.step2PixelSize
    } : {};
    $[62] = cfg.step2PixelSize;
    $[63] = t7;
  } else {
    t7 = $[63];
  }
  let t8;
  if ($[64] !== kind || $[65] !== palette || $[66] !== selectedVariant || $[67] !== t6 || $[68] !== t7) {
    t8 = <VariantPreview kind={kind} variant={selectedVariant} seed={t6} paletteName={palette} {...t7} />;
    $[64] = kind;
    $[65] = palette;
    $[66] = selectedVariant;
    $[67] = t6;
    $[68] = t7;
    $[69] = t8;
  } else {
    t8 = $[69];
  }
  let t9;
  if ($[70] !== cfg.previewWrapperClassName || $[71] !== t8) {
    t9 = <div className={t5}>{t8}</div>;
    $[70] = cfg.previewWrapperClassName;
    $[71] = t8;
    $[72] = t9;
  } else {
    t9 = $[72];
  }
  let t10;
  if ($[73] !== selectedVariant || $[74] !== variantLabel) {
    t10 = variantLabel(selectedVariant);
    $[73] = selectedVariant;
    $[74] = variantLabel;
    $[75] = t10;
  } else {
    t10 = $[75];
  }
  let t11;
  if ($[76] !== t10) {
    t11 = <p className="text-body text-foreground-secondary">{t10}</p>;
    $[76] = t10;
    $[77] = t11;
  } else {
    t11 = $[77];
  }
  let t12;
  if ($[78] !== t11 || $[79] !== t9) {
    t12 = <div className="flex flex-col items-center gap-3">{t9}{t11}</div>;
    $[78] = t11;
    $[79] = t9;
    $[80] = t12;
  } else {
    t12 = $[80];
  }
  const preview = t12;
  const t13 = genStep === 1 ? grid : preview;
  let t14;
  if ($[81] !== error) {
    t14 = error !== null && <p role="alert" className="text-body text-feedback-error-content">{error}</p>;
    $[81] = error;
    $[82] = t14;
  } else {
    t14 = $[82];
  }
  let t15;
  if ($[83] !== genStep || $[84] !== isSaving || $[85] !== onBack || $[86] !== tImageUpload) {
    t15 = genStep === 2 && <AnimatedButton type="button" variant="secondary" className="min-h-11 flex-1" disabled={isSaving} onClick={onBack}>{tImageUpload("back")}</AnimatedButton>;
    $[83] = genStep;
    $[84] = isSaving;
    $[85] = onBack;
    $[86] = tImageUpload;
    $[87] = t15;
  } else {
    t15 = $[87];
  }
  let t16;
  if ($[88] !== genStep || $[89] !== isSaving || $[90] !== onConfirm || $[91] !== onNext || $[92] !== tImageUpload || $[93] !== tNs) {
    t16 = genStep === 1 ? <AnimatedButton type="button" className="min-h-11 flex-1" onClick={onNext}>{tImageUpload("next")}</AnimatedButton> : <AnimatedButton type="button" className="min-h-11 flex-1" disabled={isSaving} loading={isSaving} onClick={onConfirm}>{isSaving ? tNs("generating") : tNs("confirm")}</AnimatedButton>;
    $[88] = genStep;
    $[89] = isSaving;
    $[90] = onConfirm;
    $[91] = onNext;
    $[92] = tImageUpload;
    $[93] = tNs;
    $[94] = t16;
  } else {
    t16 = $[94];
  }
  let t17;
  if ($[95] !== t15 || $[96] !== t16) {
    t17 = <div className="flex gap-2">{t15}{t16}</div>;
    $[95] = t15;
    $[96] = t16;
    $[97] = t17;
  } else {
    t17 = $[97];
  }
  let t18;
  if ($[98] !== t13 || $[99] !== t14 || $[100] !== t17) {
    t18 = <div className="flex flex-col gap-4">{t13}{t14}{t17}</div>;
    $[98] = t13;
    $[99] = t14;
    $[100] = t17;
    $[101] = t18;
  } else {
    t18 = $[101];
  }
  return t18;
}
interface IdentityPickerChromeProps {
  kind: IdentityImagePickerKind;
  open: boolean;
  uploadOpen: boolean;
  tab: Tab;
  genStep: 1 | 2;
  error: string | null;
  userId: string;
  currentUrl: string | null;
  currentRasterise: boolean | undefined;
  pendingUploadFile: File | null;
  title: string;
  cropTitle: string;
  successMessage: string;
  fileNameStem: string;
  tabsItems: ReadonlyArray<AppTabsItem>;
  generateBody: React.ReactNode;
  onPickerOpenChange: (next: boolean) => void;
  onTabChange: (tab: Tab) => void;
  onUploadOpenChange: (next: boolean) => void;
  onPendingUpload: (blobUrl: string) => void;
  onUploadUrlChange: (url: string) => void;
  onAvatarPreferencesChange: (prefs: {
    rasterise: boolean;
    mode: 'pattern' | 'original' | 'morfian';
  }) => void;
}
function IdentityPickerChrome(props) {
  const $ = _c(48);
  const {
    kind,
    open,
    uploadOpen,
    tab,
    genStep,
    error,
    userId,
    currentUrl,
    currentRasterise,
    pendingUploadFile,
    title,
    cropTitle,
    successMessage,
    fileNameStem,
    tabsItems,
    generateBody,
    onPickerOpenChange,
    onTabChange,
    onUploadOpenChange,
    onPendingUpload,
    onUploadUrlChange,
    onAvatarPreferencesChange
  } = props;
  const t0 = open && !uploadOpen;
  let t1;
  if ($[0] !== error || $[1] !== genStep || $[2] !== tab) {
    t1 = [tab, genStep, error];
    $[0] = error;
    $[1] = genStep;
    $[2] = tab;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== genStep || $[5] !== kind || $[6] !== tab) {
    t2 = kind !== "hero" ? <GenStepIndicator currentStep={tab === "generate" && genStep === 2 ? 2 : 1} /> : null;
    $[4] = genStep;
    $[5] = kind;
    $[6] = tab;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== genStep || $[9] !== generateBody || $[10] !== onTabChange || $[11] !== tab || $[12] !== tabsItems) {
    t3 = tab === "generate" && genStep === 2 ? generateBody : <AppTabs value={tab} onValueChange={v => {
      if (v === "generate" || v === "upload" || v === "search" || v === "morfian") {
        onTabChange(v);
      }
    }} items={tabsItems} />;
    $[8] = genStep;
    $[9] = generateBody;
    $[10] = onTabChange;
    $[11] = tab;
    $[12] = tabsItems;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== t1 || $[15] !== t2 || $[16] !== t3) {
    t4 = <MotionAnimatedAutoHeight layoutDependencies={t1} innerClassName="flex flex-col gap-4">{t2}{t3}</MotionAnimatedAutoHeight>;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== onPickerOpenChange || $[19] !== t0 || $[20] !== t4 || $[21] !== title) {
    t5 = <SilkSheet open={t0} onOpenChange={onPickerOpenChange} variant="detached" keyboard={true} size="default" title={title}>{t4}</SilkSheet>;
    $[18] = onPickerOpenChange;
    $[19] = t0;
    $[20] = t4;
    $[21] = title;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== currentRasterise || $[24] !== kind || $[25] !== onAvatarPreferencesChange) {
    t6 = kind === "avatar" ? {
      kind: "avatar" as const,
      onPreferencesChange: onAvatarPreferencesChange,
      showRasteriseToggle: true,
      previewMode: currentRasterise ?? true ? "lib" as const : "raw" as const
    } : {
      showRemove: true
    };
    $[23] = currentRasterise;
    $[24] = kind;
    $[25] = onAvatarPreferencesChange;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== fileNameStem || $[28] !== kind) {
    t7 = kind === "avatar" ? {
      aspect: 1,
      circular: true,
      outputSize: {
        w: 256,
        h: 256
      },
      fileNameStem
    } : {
      aspect: 3,
      outputSize: {
        w: 1500,
        h: 500
      },
      fileNameStem
    };
    $[27] = fileNameStem;
    $[28] = kind;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== pendingUploadFile) {
    t8 = pendingUploadFile !== null ? {
      initialFile: pendingUploadFile
    } : {};
    $[30] = pendingUploadFile;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== cropTitle || $[33] !== currentUrl || $[34] !== onPendingUpload || $[35] !== onUploadOpenChange || $[36] !== onUploadUrlChange || $[37] !== successMessage || $[38] !== t6 || $[39] !== t7 || $[40] !== t8 || $[41] !== title || $[42] !== uploadOpen || $[43] !== userId) {
    t9 = <CropModal open={uploadOpen} onOpenChange={onUploadOpenChange} userId={userId} currentUrl={currentUrl} onPendingUpload={onPendingUpload} onUrlChange={onUploadUrlChange} {...t6} crop={t7} title={title} cropTitle={cropTitle} successMessage={successMessage} {...t8} />;
    $[32] = cropTitle;
    $[33] = currentUrl;
    $[34] = onPendingUpload;
    $[35] = onUploadOpenChange;
    $[36] = onUploadUrlChange;
    $[37] = successMessage;
    $[38] = t6;
    $[39] = t7;
    $[40] = t8;
    $[41] = title;
    $[42] = uploadOpen;
    $[43] = userId;
    $[44] = t9;
  } else {
    t9 = $[44];
  }
  let t10;
  if ($[45] !== t5 || $[46] !== t9) {
    t10 = <>{t5}{t9}</>;
    $[45] = t5;
    $[46] = t9;
    $[47] = t10;
  } else {
    t10 = $[47];
  }
  return t10;
}

/**
 * Shared identity-image picker — drives the avatar AND hero pickers via a
 * `kind` discriminator. Three tabs: Search → Pattern → Upload. Search hands
 * a fetched File to CropModal exactly like the Upload tab. The
 * generative-Pattern flow renders a live preview through `<OptimizedImage>`
 * so the user sees the formatted artefact every visitor will see.
 */
export function IdentityImagePicker(t0) {
  const $ = _c(155);
  const {
    kind,
    open,
    onOpenChange,
    userId,
    searchSeedQuery,
    currentUrl,
    currentAvatarNsfw,
    onChange,
    currentRasterise,
    morfianStoredSeed
  } = t0;
  const tNs = useTranslations(`${kind}_picker`);
  const tLegacy = useTranslations(kind === "avatar" ? "profile.avatar" : "profile.hero");
  const cfg = configFor(kind);
  const morfianEnabled = kind === "avatar" && checkGate("morfian_avatar");
  const [tab, setTab] = useState(morfianEnabled ? "morfian" : "search");
  const [genStep, setGenStep] = useState(1);
  let t1;
  if ($[0] !== kind) {
    t1 = () => defaultVariantFor(kind);
    $[0] = kind;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [selectedVariant, setSelectedVariant] = useState(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [refreshCounters, setRefreshCounters] = useState(t2);
  const [heroCoverOptions, setHeroCoverOptions] = useState(null);
  const [selectedHeroCoverIndex, setSelectedHeroCoverIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingUploadFile, setPendingUploadFile] = useState(null);
  const [searchInputState, setSearchInputState] = useState("");
  const [searchQueryState, setSearchQueryState] = useState("");
  const [uploadError, setUploadError] = useState(null);
  const chosenRasteriseRef = useRef(currentRasterise ?? true);
  const chosenAvatarModeRef = useRef(currentRasterise ?? true ? "pattern" : "original");
  const lastPersistedUrlRef = useRef(currentUrl);
  const lastPersistedAvatarNsfwRef = useRef(currentAvatarNsfw ?? null);
  const wasOpenRef = useRef(false);
  let t3;
  let t4;
  if ($[3] !== currentAvatarNsfw || $[4] !== currentUrl || $[5] !== open) {
    t3 = () => {
      if (open && !wasOpenRef.current) {
        lastPersistedUrlRef.current = currentUrl;
        lastPersistedAvatarNsfwRef.current = currentAvatarNsfw ?? null;
      }
      wasOpenRef.current = open;
    };
    t4 = [currentAvatarNsfw, currentUrl, open];
    $[3] = currentAvatarNsfw;
    $[4] = currentUrl;
    $[5] = open;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== userId) {
    t5 = pickBrandBluePalette(userId);
    $[8] = userId;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const palette = t5;
  let t6;
  if ($[10] !== kind || $[11] !== refreshCounters || $[12] !== userId) {
    t6 = function seedFor(variant) {
      const counter = refreshCounters[variant] ?? 0;
      return hashStringToSeed(`${userId}:${kind}:${variant}:${String(counter)}`);
    };
    $[10] = kind;
    $[11] = refreshCounters;
    $[12] = userId;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const seedFor = t6;
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = function refreshVariant(variant_0) {
      setRefreshCounters(prev => ({
        ...prev,
        [variant_0]: (prev[variant_0] ?? 0) + 1
      }));
    };
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const refreshVariant = t7;
  let t8;
  if ($[15] !== setError) {
    t8 = function randomiseHeroCover() {
      setHeroCoverOptions(generateHeroCoverPickerOptions());
      setSelectedHeroCoverIndex(0);
      setError(null);
    };
    $[15] = setError;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const randomiseHeroCover = t8;
  let t9;
  if ($[17] !== kind || $[18] !== open || $[19] !== setError) {
    t9 = () => {
      if (kind !== "hero") {
        return;
      }
      if (open) {
        setHeroCoverOptions(generateHeroCoverPickerOptions());
        setSelectedHeroCoverIndex(0);
        setError(null);
        return;
      }
      setHeroCoverOptions(null);
      setSelectedHeroCoverIndex(0);
    };
    $[17] = kind;
    $[18] = open;
    $[19] = setError;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== kind || $[22] !== open) {
    t10 = [open, kind];
    $[21] = kind;
    $[22] = open;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  useEffect(t9, t10);
  const t11 = kind === "hero" ? heroCoverOptions : null;
  const t12 = kind === "hero" ? selectedHeroCoverIndex : 0;
  let t13;
  if ($[24] !== kind || $[25] !== onChange || $[26] !== onOpenChange || $[27] !== seedFor || $[28] !== selectedVariant || $[29] !== setError || $[30] !== setPendingUploadFile || $[31] !== setUploadOpen || $[32] !== t11 || $[33] !== t12 || $[34] !== userId) {
    t13 = {
      kind,
      userId,
      onChange,
      onOpenChange,
      selectedVariant,
      seedFor,
      heroCoverOptions: t11,
      selectedHeroCoverIndex: t12,
      setIsSaving,
      setError,
      setUploadError,
      setPendingUploadFile,
      setUploadOpen,
      chosenRasteriseRef,
      chosenAvatarModeRef,
      lastPersistedUrlRef,
      lastPersistedAvatarNsfwRef
    };
    $[24] = kind;
    $[25] = onChange;
    $[26] = onOpenChange;
    $[27] = seedFor;
    $[28] = selectedVariant;
    $[29] = setError;
    $[30] = setPendingUploadFile;
    $[31] = setUploadOpen;
    $[32] = t11;
    $[33] = t12;
    $[34] = userId;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const actions = useIdentityPickerActions(t13);
  let t14;
  if ($[36] !== setError) {
    t14 = variant_1 => {
      setSelectedVariant(variant_1);
      setError(null);
    };
    $[36] = setError;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  const t15 = kind === "hero" ? heroCoverOptions : null;
  const t16 = kind === "hero" ? selectedHeroCoverIndex : 0;
  let t17;
  if ($[38] !== setError) {
    t17 = index => {
      setSelectedHeroCoverIndex(index);
      setError(null);
    };
    $[38] = setError;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  let t19;
  if ($[40] !== setError || $[41] !== setGenStep) {
    t18 = () => {
      setGenStep(2);
      setError(null);
    };
    t19 = () => {
      setGenStep(1);
      setError(null);
    };
    $[40] = setError;
    $[41] = setGenStep;
    $[42] = t18;
    $[43] = t19;
  } else {
    t18 = $[42];
    t19 = $[43];
  }
  let t20;
  if ($[44] !== actions) {
    t20 = () => {
      actions.handleConfirmGenerative();
    };
    $[44] = actions;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== error || $[47] !== genStep || $[48] !== isSaving || $[49] !== kind || $[50] !== palette || $[51] !== randomiseHeroCover || $[52] !== seedFor || $[53] !== selectedVariant || $[54] !== t14 || $[55] !== t15 || $[56] !== t16 || $[57] !== t17 || $[58] !== t18 || $[59] !== t19 || $[60] !== t20 || $[61] !== userId) {
    t21 = <GenerateTab kind={kind} userId={userId} palette={palette} selectedVariant={selectedVariant} onSelectVariant={t14} seedFor={seedFor} refreshVariant={refreshVariant} heroCoverOptions={t15} selectedHeroCoverIndex={t16} onSelectHeroCoverIndex={t17} onRandomiseHero={randomiseHeroCover} genStep={genStep} onNext={t18} onBack={t19} onConfirm={t20} error={error} isSaving={isSaving} />;
    $[46] = error;
    $[47] = genStep;
    $[48] = isSaving;
    $[49] = kind;
    $[50] = palette;
    $[51] = randomiseHeroCover;
    $[52] = seedFor;
    $[53] = selectedVariant;
    $[54] = t14;
    $[55] = t15;
    $[56] = t16;
    $[57] = t17;
    $[58] = t18;
    $[59] = t19;
    $[60] = t20;
    $[61] = userId;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  const generateBody = t21;
  const t22 = kind === "hero" ? "none" : "square";
  let t23;
  if ($[63] !== actions.handleUploadFile || $[64] !== t22 || $[65] !== uploadError) {
    t23 = <ImageUploadDropzone onFileSelected={actions.handleUploadFile} errorMessage={uploadError} aspectRatio={t22} />;
    $[63] = actions.handleUploadFile;
    $[64] = t22;
    $[65] = uploadError;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  const uploadBody = t23;
  const t24 = kind === "avatar" ? "" : searchSeedQuery;
  let t25;
  if ($[67] !== actions) {
    t25 = file => {
      actions.handleUploadFile(file);
    };
    $[67] = actions;
    $[68] = t25;
  } else {
    t25 = $[68];
  }
  let t26;
  if ($[69] !== kind || $[70] !== searchInputState || $[71] !== searchQueryState || $[72] !== t24 || $[73] !== t25) {
    t26 = <SearchTab initialQuery={t24} mode={kind} searchInput={searchInputState} onSearchInputChange={setSearchInputState} searchQuery={searchQueryState} onSearchQueryChange={setSearchQueryState} onPick={t25} />;
    $[69] = kind;
    $[70] = searchInputState;
    $[71] = searchQueryState;
    $[72] = t24;
    $[73] = t25;
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  const searchBody = t26;
  const T0 = MorfianTab;
  const t27 = shouldFetchImageQuota({
    open,
    generationEnabled: morfianEnabled && tab === "morfian"
  });
  let t28;
  if ($[75] !== morfianStoredSeed) {
    t28 = morfianStoredSeed !== undefined ? {
      storedSeed: morfianStoredSeed
    } : {};
    $[75] = morfianStoredSeed;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  let t29;
  if ($[77] !== actions) {
    t29 = result => {
      actions.handleMorfianConfirm(result);
    };
    $[77] = actions;
    $[78] = t29;
  } else {
    t29 = $[78];
  }
  let t30;
  if ($[79] !== T0 || $[80] !== t27 || $[81] !== t28 || $[82] !== t29) {
    t30 = <T0 quotaEnabled={t27} {...t28} onConfirm={t29} />;
    $[79] = T0;
    $[80] = t27;
    $[81] = t28;
    $[82] = t29;
    $[83] = t30;
  } else {
    t30 = $[83];
  }
  const morfianBody = t30;
  let t31;
  if ($[84] !== morfianBody || $[85] !== morfianEnabled || $[86] !== tNs) {
    t31 = morfianEnabled ? [{
      value: "morfian",
      label: tNs("tab_morfian"),
      icon: SparklesIcon,
      content: morfianBody
    }] as const : [];
    $[84] = morfianBody;
    $[85] = morfianEnabled;
    $[86] = tNs;
    $[87] = t31;
  } else {
    t31 = $[87];
  }
  let t32;
  if ($[88] !== tNs) {
    t32 = tNs("tab_search");
    $[88] = tNs;
    $[89] = t32;
  } else {
    t32 = $[89];
  }
  let t33;
  if ($[90] !== searchBody || $[91] !== t32) {
    t33 = {
      value: "search",
      label: t32,
      icon: Search01Icon,
      content: searchBody
    };
    $[90] = searchBody;
    $[91] = t32;
    $[92] = t33;
  } else {
    t33 = $[92];
  }
  let t34;
  if ($[93] !== generateBody || $[94] !== kind || $[95] !== tNs) {
    t34 = kind === "hero" ? [{
      value: "generate",
      label: tNs("tab_generate"),
      icon: PaintBoardIcon,
      content: generateBody
    }] as const : [];
    $[93] = generateBody;
    $[94] = kind;
    $[95] = tNs;
    $[96] = t34;
  } else {
    t34 = $[96];
  }
  let t35;
  if ($[97] !== tNs) {
    t35 = tNs("tab_upload");
    $[97] = tNs;
    $[98] = t35;
  } else {
    t35 = $[98];
  }
  let t36;
  if ($[99] !== t35 || $[100] !== uploadBody) {
    t36 = {
      value: "upload",
      label: t35,
      icon: Upload01Icon,
      content: uploadBody
    };
    $[99] = t35;
    $[100] = uploadBody;
    $[101] = t36;
  } else {
    t36 = $[101];
  }
  let t37;
  if ($[102] !== t31 || $[103] !== t33 || $[104] !== t34 || $[105] !== t36) {
    t37 = [...t31, t33, ...t34, t36];
    $[102] = t31;
    $[103] = t33;
    $[104] = t34;
    $[105] = t36;
    $[106] = t37;
  } else {
    t37 = $[106];
  }
  const tabsItems = t37;
  const T1 = IdentityPickerChrome;
  let t38;
  if ($[107] !== tLegacy) {
    t38 = tLegacy("title");
    $[107] = tLegacy;
    $[108] = t38;
  } else {
    t38 = $[108];
  }
  let t39;
  if ($[109] !== tLegacy) {
    t39 = tLegacy("crop_title");
    $[109] = tLegacy;
    $[110] = t39;
  } else {
    t39 = $[110];
  }
  let t40;
  if ($[111] !== tLegacy) {
    t40 = tLegacy("success");
    $[111] = tLegacy;
    $[112] = t40;
  } else {
    t40 = $[112];
  }
  const t41 = cfg.fileNameStem(userId);
  let t42;
  if ($[113] !== onOpenChange || $[114] !== setError || $[115] !== setGenStep) {
    t42 = next => {
      onOpenChange(next);
      if (!next) {
        setGenStep(1);
        setError(null);
      }
    };
    $[113] = onOpenChange;
    $[114] = setError;
    $[115] = setGenStep;
    $[116] = t42;
  } else {
    t42 = $[116];
  }
  let t43;
  if ($[117] !== setError || $[118] !== setGenStep || $[119] !== setTab) {
    t43 = nextTab => {
      setTab(nextTab);
      setError(null);
      setGenStep(1);
    };
    $[117] = setError;
    $[118] = setGenStep;
    $[119] = setTab;
    $[120] = t43;
  } else {
    t43 = $[120];
  }
  let t44;
  if ($[121] !== setPendingUploadFile || $[122] !== setUploadOpen) {
    t44 = next_0 => {
      setUploadOpen(next_0);
      if (!next_0) {
        setPendingUploadFile(null);
      }
    };
    $[121] = setPendingUploadFile;
    $[122] = setUploadOpen;
    $[123] = t44;
  } else {
    t44 = $[123];
  }
  let t45;
  if ($[124] !== onChange || $[125] !== onOpenChange || $[126] !== setPendingUploadFile || $[127] !== setUploadOpen) {
    t45 = blobUrl => {
      onChange(blobUrl);
      setUploadOpen(false);
      setPendingUploadFile(null);
      onOpenChange(false);
    };
    $[124] = onChange;
    $[125] = onOpenChange;
    $[126] = setPendingUploadFile;
    $[127] = setUploadOpen;
    $[128] = t45;
  } else {
    t45 = $[128];
  }
  let t46;
  if ($[129] !== actions) {
    t46 = url => {
      actions.handleUploadUrlChange(url);
    };
    $[129] = actions;
    $[130] = t46;
  } else {
    t46 = $[130];
  }
  let t47;
  if ($[131] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = prefs => {
      chosenRasteriseRef.current = prefs.rasterise;
      chosenAvatarModeRef.current = prefs.mode;
    };
    $[131] = t47;
  } else {
    t47 = $[131];
  }
  let t48;
  if ($[132] !== T1 || $[133] !== currentRasterise || $[134] !== currentUrl || $[135] !== error || $[136] !== genStep || $[137] !== generateBody || $[138] !== kind || $[139] !== open || $[140] !== pendingUploadFile || $[141] !== t38 || $[142] !== t39 || $[143] !== t40 || $[144] !== t41 || $[145] !== t42 || $[146] !== t43 || $[147] !== t44 || $[148] !== t45 || $[149] !== t46 || $[150] !== tab || $[151] !== tabsItems || $[152] !== uploadOpen || $[153] !== userId) {
    t48 = <T1 kind={kind} open={open} uploadOpen={uploadOpen} tab={tab} genStep={genStep} error={error} userId={userId} currentUrl={currentUrl} currentRasterise={currentRasterise} pendingUploadFile={pendingUploadFile} title={t38} cropTitle={t39} successMessage={t40} fileNameStem={t41} tabsItems={tabsItems} generateBody={generateBody} onPickerOpenChange={t42} onTabChange={t43} onUploadOpenChange={t44} onPendingUpload={t45} onUploadUrlChange={t46} onAvatarPreferencesChange={t47} />;
    $[132] = T1;
    $[133] = currentRasterise;
    $[134] = currentUrl;
    $[135] = error;
    $[136] = genStep;
    $[137] = generateBody;
    $[138] = kind;
    $[139] = open;
    $[140] = pendingUploadFile;
    $[141] = t38;
    $[142] = t39;
    $[143] = t40;
    $[144] = t41;
    $[145] = t42;
    $[146] = t43;
    $[147] = t44;
    $[148] = t45;
    $[149] = t46;
    $[150] = tab;
    $[151] = tabsItems;
    $[152] = uploadOpen;
    $[153] = userId;
    $[154] = t48;
  } else {
    t48 = $[154];
  }
  return t48;
}
interface VariantPreviewProps {
  kind: IdentityImagePickerKind;
  variant: GenerativeVariant;
  seed: number | string;
  /** Hero only — passthrough cover entity uses this for placeholder fallback. */
  userId?: string;
  /** Avatar only — drives mosaic palette at display time. */
  paletteName?: PaletteName;
  coverOverrides?: DotCoverOverrides;
  /**
   * Avatar only — drives both the OptimizedImage `size` prop and the
   * source PNG resolution. Defaults to 64 px for the variant grid; the
   * post-select preview passes 224 to fill the larger circle.
   */
  pixelSize?: number;
}

/**
 * Live thumbnail preview for a single generative-variant card.
 *
 * Hero covers: generate the HTML DotEngine's final colored PNG via
 * `generateDotStudioCoverPng` and render it raw through a passthrough
 * `cover` entity — no lib re-grade or dither, so preview == exported.
 *
 * Avatars: generate a low-res grayscale source PNG via
 * `generateGenerativePng` then feed it to
 * `<OptimizedImage entity={{ kind: 'preview' }}>` so the user sees the
 * formatted artefact (lib palette + dither) — identical to what every
 * visitor will see after upload.
 */
function VariantPreview(props) {
  const $ = _c(55);
  const tImage = useTranslations("image_upload");
  const tNs = useTranslations(`${props.kind}_picker`);
  let cfg;
  let isHero;
  let pixelSize;
  let t0;
  if ($[0] !== props.kind || $[1] !== props.pixelSize) {
    cfg = configFor(props.kind);
    isHero = props.kind === "hero";
    pixelSize = props.pixelSize ?? cfg.variantPreviewDefaultPixelSize;
    t0 = cfg.variantPreviewSource(pixelSize);
    $[0] = props.kind;
    $[1] = props.pixelSize;
    $[2] = cfg;
    $[3] = isHero;
    $[4] = pixelSize;
    $[5] = t0;
  } else {
    cfg = $[2];
    isHero = $[3];
    pixelSize = $[4];
    t0 = $[5];
  }
  const {
    width: sourceWidth,
    height: sourceHeight
  } = t0;
  const [blobUrl, setBlobUrl] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  let t1;
  let t2;
  if ($[6] !== isHero || $[7] !== props.coverOverrides || $[8] !== props.kind || $[9] !== props.seed || $[10] !== props.variant || $[11] !== sourceHeight || $[12] !== sourceWidth) {
    t1 = () => {
      let revoked = false;
      let createdUrl = null;
      const generate = isHero ? generateDotStudioCoverPng(props.variant, props.seed, {
        width: sourceWidth,
        height: sourceHeight,
        mimeType: "image/png",
        ...(props.coverOverrides !== undefined ? {
          overrides: props.coverOverrides
        } : {})
      }) : generateGenerativePng(props.variant, props.seed, {
        width: sourceWidth,
        height: sourceHeight,
        mimeType: "image/png"
      });
      generate.then(blob => {
        if (revoked) {
          return;
        }
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      }).catch(err => {
        if (revoked) {
          return;
        }
        setPreviewError(true);
        const message = err instanceof Error ? err.message : String(err);
        logger.warn("Identity variant preview generation failed", {
          error: message.slice(0, 200),
          variant: props.variant,
          kind: props.kind
        });
      });
      return () => {
        revoked = true;
        if (createdUrl !== null) {
          URL.revokeObjectURL(createdUrl);
        }
      };
    };
    t2 = [props.variant, props.seed, props.kind, props.coverOverrides, isHero, sourceWidth, sourceHeight];
    $[6] = isHero;
    $[7] = props.coverOverrides;
    $[8] = props.kind;
    $[9] = props.seed;
    $[10] = props.variant;
    $[11] = sourceHeight;
    $[12] = sourceWidth;
    $[13] = t1;
    $[14] = t2;
  } else {
    t1 = $[13];
    t2 = $[14];
  }
  useEffect(t1, t2);
  if (previewError) {
    let t3;
    if ($[15] !== tNs) {
      t3 = tNs("generate_error");
      $[15] = tNs;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    let t4;
    if ($[17] !== t3) {
      t4 = <div role="alert" className="absolute inset-0 flex items-center justify-center bg-background-tertiary px-4 text-center text-caption text-feedback-error-content">{t3}</div>;
      $[17] = t3;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    return t4;
  }
  if (blobUrl === null) {
    let t3;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-br from-background-tertiary to-background-secondary" />;
      $[19] = t3;
    } else {
      t3 = $[19];
    }
    let t4;
    if ($[20] !== tNs) {
      t4 = tNs("generating");
      $[20] = tNs;
      $[21] = t4;
    } else {
      t4 = $[21];
    }
    let t5;
    if ($[22] !== t4) {
      t5 = <>{t3}<span className="sr-only">{t4}</span></>;
      $[22] = t4;
      $[23] = t5;
    } else {
      t5 = $[23];
    }
    return t5;
  }
  if (isHero) {
    const t3 = cfg.optimizedImageVariant;
    const t4 = props.userId ?? "";
    let t5;
    if ($[24] !== blobUrl || $[25] !== t4) {
      t5 = {
        kind: "cover",
        userId: t4,
        coverUrl: blobUrl,
        coverRender: "passthrough"
      };
      $[24] = blobUrl;
      $[25] = t4;
      $[26] = t5;
    } else {
      t5 = $[26];
    }
    let t6;
    if ($[27] !== tImage) {
      t6 = tImage("preview_alt");
      $[27] = tImage;
      $[28] = t6;
    } else {
      t6 = $[28];
    }
    let t7;
    if ($[29] !== cfg.optimizedImageExtraClass) {
      t7 = cfg.optimizedImageExtraClass.length > 0 ? {
        imageClassName: cfg.optimizedImageExtraClass
      } : {};
      $[29] = cfg.optimizedImageExtraClass;
      $[30] = t7;
    } else {
      t7 = $[30];
    }
    let t8;
    if ($[31] !== cfg.optimizedImageVariant || $[32] !== t5 || $[33] !== t6 || $[34] !== t7) {
      t8 = <OptimizedImage variant={t3} entity={t5} alt={t6} className="absolute inset-0 size-full" {...t7} nsfw={false} preview={false} />;
      $[31] = cfg.optimizedImageVariant;
      $[32] = t5;
      $[33] = t6;
      $[34] = t7;
      $[35] = t8;
    } else {
      t8 = $[35];
    }
    return t8;
  }
  if (props.paletteName === undefined) {
    throw new Error("Identity avatar preview: paletteName is required");
  }
  const t3 = cfg.optimizedImageVariant;
  let t4;
  if ($[36] !== props.paletteName) {
    t4 = {
      kind: "preset",
      name: props.paletteName
    };
    $[36] = props.paletteName;
    $[37] = t4;
  } else {
    t4 = $[37];
  }
  let t5;
  if ($[38] !== props.seed) {
    t5 = typeof props.seed === "number" ? props.seed : hashStringToSeed(String(props.seed));
    $[38] = props.seed;
    $[39] = t5;
  } else {
    t5 = $[39];
  }
  let t6;
  if ($[40] !== blobUrl || $[41] !== t4 || $[42] !== t5) {
    t6 = {
      kind: "preview",
      sourceBlobUrl: blobUrl,
      paletteRef: t4,
      seed: t5
    };
    $[40] = blobUrl;
    $[41] = t4;
    $[42] = t5;
    $[43] = t6;
  } else {
    t6 = $[43];
  }
  let t7;
  if ($[44] !== tImage) {
    t7 = tImage("preview_alt");
    $[44] = tImage;
    $[45] = t7;
  } else {
    t7 = $[45];
  }
  let t8;
  if ($[46] !== cfg.optimizedImageExtraClass) {
    t8 = cfg.optimizedImageExtraClass.length > 0 ? {
      imageClassName: cfg.optimizedImageExtraClass
    } : {};
    $[46] = cfg.optimizedImageExtraClass;
    $[47] = t8;
  } else {
    t8 = $[47];
  }
  let t9;
  if ($[48] !== cfg.optimizedImageRounded || $[49] !== cfg.optimizedImageVariant || $[50] !== pixelSize || $[51] !== t6 || $[52] !== t7 || $[53] !== t8) {
    t9 = <OptimizedImage variant={t3} entity={t6} alt={t7} size={pixelSize} className="absolute inset-0 size-full" {...t8} rounded={cfg.optimizedImageRounded} nsfw={false} preview={false} />;
    $[48] = cfg.optimizedImageRounded;
    $[49] = cfg.optimizedImageVariant;
    $[50] = pixelSize;
    $[51] = t6;
    $[52] = t7;
    $[53] = t8;
    $[54] = t9;
  } else {
    t9 = $[54];
  }
  return t9;
}
function GenStepIndicator(t0) {
  const $ = _c(3);
  const {
    currentStep
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [1, 2];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== currentStep) {
    t2 = <div className="flex gap-1" aria-hidden="true">{t1.map(i => <div key={`gen-step-${String(i)}`} className={cn("h-1.5 flex-1 rounded-full transition-colors", i <= currentStep ? "bg-foreground-primary" : "bg-background-tertiary")} />)}</div>;
    $[1] = currentStep;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}